import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileEdit, ArrowLeft, Copy, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/company";
import { convertFromAED, formatMoney } from "@/lib/currencies";

const formatCurrency = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const QuoteViewPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);

  useEffect(() => {
    apiClient
      .get(`/quotes/${id}`)
      .then(({ data }) => setQ(data))
      .catch(() => {
        toast.error("Quote not found");
        nav("/");
      });
  }, [id, nav]);

  if (!q) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  const subTotal = q.line_items.reduce(
    (s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0),
    0
  );
  const chargesTaxable = (q.additional_charges || [])
    .filter((c) => !c.vat_exempt)
    .reduce((s, c) => s + Number(c.amount || 0), 0);
  const chargesExempt = (q.additional_charges || [])
    .filter((c) => c.vat_exempt)
    .reduce((s, c) => s + Number(c.amount || 0), 0);
  const itemsTaxable = q.items_vat_exempt ? 0 : subTotal;
  const taxableBase = itemsTaxable + chargesTaxable;
  const vatAmount = q.vat_mode === "exclude" ? taxableBase * (Number(q.vat_percent) / 100) : 0;
  const grandTotal = subTotal + chargesTaxable + chargesExempt + vatAmount;

  const currency = q.currency || "AED";
  const fxRate = currency === "AED" ? 1 : Number(q.fx_rate) || 0;
  const showConversion = currency !== "AED" && fxRate > 0;
  const conv = (aed) => convertFromAED(aed, fxRate, currency);
  const fmt = (aed) => formatMoney(aed, "AED");
  const isMultiOption = (q.options && q.options.length > 1);

  const sanitize = (s) => (s || "").replace(/[/\\?%*:|"<>]/g, "").trim().replace(/\s+/g, " ");

  const handlePrint = () => {
    const filename = `${sanitize(q.customer_name)}-${sanitize(q.quote_ref)}-${q.quote_date || ""}`.trim();
    const original = document.title;
    document.title = filename || original;
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = original; }, 1000);
    }, 50);
  };

  return (
    <div className="bg-slate-100 p-0">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-[#E5E7EB] bg-white px-6 py-3">
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => nav("/")} data-testid="back-to-dashboard">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={async () => {
              try {
                const { data } = await apiClient.post(`/quotes/${q.id}/revise`);
                toast.success(`Revised as ${data.quote_ref}`);
                nav(`/quotes/${data.id}/edit`);
              } catch {
                toast.error("Revise failed");
              }
            }}
            data-testid="revise-quote-button"
          >
            <GitBranch className="mr-2 h-4 w-4" /> Revise
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={async () => {
              try {
                const { data } = await apiClient.post(`/quotes/${q.id}/duplicate`);
                toast.success(`Duplicated as ${data.quote_ref}`);
                nav(`/quotes/${data.id}/edit`);
              } catch {
                toast.error("Duplicate failed");
              }
            }}
            data-testid="duplicate-quote-button"
          >
            <Copy className="mr-2 h-4 w-4" /> Duplicate
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-sm" data-testid="edit-quote-button">
            <Link to={`/quotes/${q.id}/edit`}>
              <FileEdit className="mr-2 h-4 w-4" /> Edit
            </Link>
          </Button>
          <Button size="sm" className="rounded-sm ep-amber hover:ep-amber" onClick={handlePrint} data-testid="print-quote-button">
            <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      <div className="mx-auto my-6 bg-white shadow-md pdf-page" data-testid="quote-preview">
        {/* Letterhead */}
        <div className="flex items-center justify-between gap-6 border-b-2 border-[#1E40AF] pb-4">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logoSrc} alt="Excellent Plastic" className="h-16 w-16 object-contain" />
            <div className="font-heading text-2xl font-extrabold uppercase leading-none text-[#1E40AF]">
              Excellent Plastic
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold uppercase tracking-wider text-[#1E40AF]">Quotation</div>
            <div className="mt-2 text-xs">
              <div>
                <span className="text-slate-500">Ref:</span>{" "}
                <span className="font-semibold">{q.quote_ref}</span>
              </div>
              <div>
                <span className="text-slate-500">Date:</span> {formatDate(q.quote_date)}
              </div>
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="mt-5 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Address</div>
            <div className="mt-1 font-bold text-slate-800">M/S, {q.customer_name}</div>
            {q.customer_place && <div className="text-slate-600">{q.customer_place}</div>}
            {q.customer_details && <div className="whitespace-pre-wrap text-slate-600">{q.customer_details}</div>}
            {q.customer_contact && <div className="text-slate-600">Tel: {q.customer_contact}</div>}
          </div>
          {q.kind_attention && (
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Kind Attention</div>
              <div className="mt-1 font-medium text-slate-800">{q.kind_attention}</div>
            </div>
          )}
        </div>

        {/* Subject */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</div>
          <div className="mt-1 border-l-4 border-[#22C55E] pl-3 text-sm font-semibold text-slate-800">{q.subject}</div>
        </div>

        {/* Welcome */}
        {q.welcome_message && (
          <p className="mt-4 text-sm leading-relaxed text-slate-700">
            Dear Sir/Madam,
            <br />
            {q.welcome_message}
          </p>
        )}

        {/* Items pricing table */}
        <div className="mt-5 border border-[#E5E7EB]" data-testid="preview-items-table">
          <div className="grid grid-cols-12 bg-[#1E40AF] text-white">
            <div className="col-span-1 border-r border-[#1E40AF] px-2 py-2 text-left text-xs font-semibold uppercase">SI.No</div>
            <div className="col-span-6 border-r border-[#1E40AF] px-2 py-2 text-left text-xs font-semibold uppercase">Description</div>
            <div className="col-span-1 border-r border-[#1E40AF] px-2 py-2 text-right text-xs font-semibold uppercase">Qty</div>
            <div className="col-span-2 border-r border-[#1E40AF] px-2 py-2 text-right text-xs font-semibold uppercase">Unit Price (AED)</div>
            <div className="col-span-2 px-2 py-2 text-right text-xs font-semibold uppercase">Total (AED)</div>
          </div>

          {(() => {
            // Group items by option. Fallback: single implicit option for legacy quotes.
            const opts = (q.options && q.options.length > 0)
              ? q.options
              : [{ id: "__legacy__", name: "" }];
            const isMultiOption = (q.options && q.options.length > 1);

            const rows = [];
            let runningIdx = 0;
            opts.forEach((opt, oIdx) => {
              const items = q.line_items.filter((li) => {
                if (opt.id === "__legacy__") return true;
                return (li.option_id || null) === opt.id;
              });
              if (items.length === 0 && opt.id !== "__legacy__") return;

              const optSubtotal = items.reduce(
                (s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0),
                0
              );

              if (isMultiOption) {
                rows.push(
                  <div
                    key={`opt-${oIdx}`}
                    className="grid grid-cols-12 border-t border-[#E5E7EB] bg-[#1E40AF]/10"
                    data-testid={`preview-option-header-${oIdx}`}
                  >
                    <div className="col-span-12 px-3 py-2 text-sm font-bold uppercase tracking-wider text-[#1E40AF]">
                      {opt.name || `Option ${oIdx + 1}`}
                    </div>
                  </div>
                );
              }

              items.forEach((li) => {
                runningIdx += 1;
                const localIdx = runningIdx;
                rows.push(
                  <div key={`item-${oIdx}-${localIdx}`} className="border-t border-[#E5E7EB]" data-testid={`preview-item-${localIdx - 1}`}>
                    <div className="grid grid-cols-12">
                      <div className="col-span-1 border-r border-[#E5E7EB] px-2 py-2 text-sm text-slate-500">{localIdx}</div>
                      <div className="col-span-6 border-r border-[#E5E7EB] px-2 py-2 text-sm">
                        <div className="whitespace-pre-wrap font-medium text-slate-800">{li.description}</div>
                        {(() => {
                          const imgs = (li.images && li.images.length > 0) ? li.images : (li.image ? [li.image] : []);
                          if (imgs.length === 0) return null;
                          return (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {imgs.map((src, i) => (
                                <img
                                  key={i}
                                  src={src}
                                  alt=""
                                  style={{ width: "8cm", height: "7cm", objectFit: "contain", display: "block", background: "#fff" }}
                                  className="border border-[#E5E7EB]"
                                />
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="col-span-1 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">{li.quantity}</div>
                      <div className="col-span-2 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">{formatCurrency(li.unit_price)}</div>
                      <div className="col-span-2 px-2 py-2 text-right text-sm font-semibold tabular-nums">{formatCurrency(Number(li.quantity) * Number(li.unit_price))}</div>
                    </div>

                    {(li.specs || []).length > 0 && (
                      <div className="border-t border-[#E5E7EB] p-3">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          Data Sheet — Item {localIdx}
                        </div>
                        <table className="w-full border-collapse text-[12px]">
                          <tbody>
                            {li.specs.map((sp, sIdx) => (
                              <tr key={sIdx}>
                                <td className="w-48 border border-[#E5E7EB] bg-slate-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  {sp.label || "—"}
                                </td>
                                <td className="border border-[#E5E7EB] px-2 py-1 text-slate-700">
                                  {sp.bullet ? (
                                    <ul className="list-disc space-y-0.5 pl-4">
                                      {(sp.value || "")
                                        .split("\n")
                                        .map((l) => l.trim())
                                        .filter(Boolean)
                                        .map((line, li2) => (
                                          <li key={li2}>{line}</li>
                                        ))}
                                      {!(sp.value || "").trim() && <li>—</li>}
                                    </ul>
                                  ) : (
                                    sp.value || "—"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              });

              if (isMultiOption) {
                rows.push(
                  <div
                    key={`sub-${oIdx}`}
                    className="grid grid-cols-12 border-t border-[#E5E7EB] bg-slate-50"
                    data-testid={`preview-option-subtotal-${oIdx}`}
                  >
                    <div className="col-span-10 border-r border-[#E5E7EB] px-3 py-1.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-700">
                      {opt.name || `Option ${oIdx + 1}`} — Subtotal
                      {showConversion && (
                        <span className="ml-2 text-[10px] font-normal normal-case text-slate-500">
                          ≈ {formatMoney(conv(optSubtotal), currency)} {currency}
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 px-2 py-1.5 text-right text-sm font-bold tabular-nums text-[#1E40AF]">
                      {formatCurrency(optSubtotal)}
                    </div>
                  </div>
                );
              }
            });
            return rows;
          })()}

          {/* Additional charges rendered as extra rows in the items table (only when a single option) */}
          {!isMultiOption && (q.additional_charges || []).map((c, i) => (
            <div key={`ch-${i}`} className="grid grid-cols-12 border-t border-[#E5E7EB]" data-testid={`preview-charge-item-${i}`}>
              <div className="col-span-1 border-r border-[#E5E7EB] px-2 py-2 text-sm text-slate-500">{q.line_items.length + i + 1}</div>
              <div className="col-span-6 border-r border-[#E5E7EB] px-2 py-2 text-sm">
                <div className="font-medium text-slate-800">{c.label || "Additional Charge"}</div>
                {c.vat_exempt && (
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">VAT-free</div>
                )}
              </div>
              <div className="col-span-1 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">—</div>
              <div className="col-span-2 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">—</div>
              <div className="col-span-2 px-2 py-2 text-right text-sm font-semibold tabular-nums">{formatCurrency(c.amount)}</div>
            </div>
          ))}
        </div>

        {/* Totals — hidden when multi-option so each option remains independent */}
        {!isMultiOption && (
        <div className="mt-3 ml-auto w-full max-w-md border border-[#E5E7EB] text-sm">
          <div className="flex items-center justify-between border-b border-[#E5E7EB] px-3 py-1.5">
            <span className="text-xs uppercase tracking-wider text-slate-600">
              Subtotal (Items) {q.items_vat_exempt && <span className="text-slate-400">(VAT-free)</span>}
            </span>
            <span className="tabular-nums">{fmt(subTotal)}</span>
          </div>
          {(q.additional_charges || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between border-b border-[#E5E7EB] px-3 py-1.5">
              <span className="text-xs uppercase tracking-wider text-slate-600">
                {c.label || "Charge"} {c.vat_exempt && <span className="text-slate-400">(VAT-free)</span>}
              </span>
              <span className="tabular-nums">{fmt(c.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-b border-[#E5E7EB] px-3 py-1.5">
            <span className="text-xs uppercase tracking-wider text-slate-600">{taxableBase === 0 ? "VAT Free (0%)" : `VAT (${q.vat_percent}%) ${q.vat_mode === "include" ? "(included)" : "(extra)"}`}</span>
            <span className="tabular-nums">{fmt(vatAmount)}</span>
          </div>
          <div className="flex items-center justify-between bg-slate-100 px-3 py-2">
            <span className="text-sm font-bold uppercase tracking-wider text-[#1E40AF]">Grand Total (AED)</span>
            <span className="text-base font-bold tabular-nums text-[#1E40AF]">{fmt(grandTotal)}</span>
          </div>
          {showConversion && (
            <div className="border-t-2 border-[#1E40AF] bg-[#F1F5F9] px-3 py-2" data-testid="quote-currency-conversion">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold uppercase tracking-wider text-[#1E40AF]">Grand Total ({currency})</span>
                <span className="text-base font-bold tabular-nums text-[#1E40AF]">
                  {formatMoney(conv(grandTotal), currency)} {currency}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">
                Bank rate used: 1 AED = {Number(fxRate).toFixed(6)} {currency}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Terms */}
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Terms &amp; Conditions</div>
          <table className="mt-2 w-full border-collapse text-sm">
            <tbody>
              <Term label="Payment Terms" value={q.payment_terms} />
              <Term label="Delivery / Lead Time" value={q.lead_time} />
              <Term label="Quote Validity" value={q.valid_until} />
              <Term
                label="VAT"
                value={`${q.vat_percent}% ${
                  q.vat_mode === "exclude" ? "extra (not included in unit price)" : "included in unit price"
                }`}
              />
              <Term label="Currency" value={q.currency} />
            </tbody>
          </table>
        </div>

        {/* Closing */}
        {q.closing_message && <p className="mt-5 text-sm leading-relaxed text-slate-700">{q.closing_message}</p>}

        {/* Signature */}
        <div className="mt-6 text-sm">
          <div className="flex items-start">
            <div className="text-slate-700">Thanks &amp; Best Regards,</div>
            <img
              src={COMPANY.sealSrc}
              alt="Company Seal"
              className="ml-[2cm] h-24 w-auto shrink-0 object-contain"
              data-testid="quote-seal"
            />
          </div>
          <div className="mt-3 font-semibold text-[#1E40AF]">{q.owner_name}</div>
          {q.owner_designation && <div className="text-xs text-slate-600">{q.owner_designation} – Excellent Plastic LLC</div>}
          <div className="mt-1 text-xs text-slate-600">
            P.O. Box {COMPANY.poBox}, Dubai – UAE
            <br />
            {q.owner_phone && <>Tel: {q.owner_phone} &nbsp;•&nbsp; </>}
            Email: {q.owner_email}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-[#E5E7EB] pt-3 text-center text-[9px] uppercase tracking-[0.2em] text-slate-400">
          This is a computer generated quotation.
        </div>
      </div>
    </div>
  );
};

const Term = ({ label, value }) => (
  <tr>
    <td className="w-48 border border-[#E5E7EB] bg-slate-50 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
      {label}
    </td>
    <td className="border border-[#E5E7EB] px-2 py-1.5 text-slate-700">{value || "—"}</td>
  </tr>
);

export default QuoteViewPage;
